import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetBlogs } from "../../hooks/blog-posts/useGetBlogs";
import { useDeleteBlog } from "../../hooks/blog-posts/useDeleteBlog";
import { useDeleteAllBlogs } from "../../hooks/blog-posts/useDeleteAllBlogs";
import { useRestoreAllBlogs } from "../../hooks/blog-posts/useRestoreAllBlogs";
import { useForceDeleteAllBlogs } from "../../hooks/blog-posts/useForceDeleteAllBlogs";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import BlogPostsTableRow from "./BlogPostsTableRow";
import { BlogPostsTableColumns } from "./BlogPostsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const BlogPostsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("blogs.view"),
            create: has("blogs.create"),
            edit: has("blogs.update"),
            delete: has("blogs.delete"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const [trashedFilter, setTrashedFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const { deleteBlogMutate, isPending: isDelete } = useDeleteBlog();
    const { deleteAllBlogsMutate, isPending: isDeleteAll } = useDeleteAllBlogs();
    const { restoreAllBlogsMutate, isPending: isRestoreAll } = useRestoreAllBlogs();
    const { forceDeleteAllBlogsMutate, isPending: isForceDeleteAll } = useForceDeleteAllBlogs();

    const {
        page, perPage, search, order, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: "/blog",
        deleteSingle: deleteBlogMutate,
        deleteAll: deleteAllBlogsMutate,
        restoreSingle: (id, options) => restoreAllBlogsMutate({ items: [id] }, options),
        restoreAll: restoreAllBlogsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllBlogsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllBlogsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page, per_page: perPage, search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            show_in_home: statusFilter || undefined,
            sort_by: 'created_at', sort_order: order,
        }),
        [page, perPage, search, trashedFilter, statusFilter, order]
    );

    const { isLoading, error, blogs, meta } = useGetBlogs(params);
    const columns = useMemo(() => BlogPostsTableColumns(isArabic), [isArabic]);

    const filters = useMemo(() => [
        {
            key: "show_in_home",
            label: { ar: "عرض في الرئيسية", en: "Show in Home" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "معروض", en: "Yes" } },
                { value: "0", label: { ar: "مخفي", en: "No" } },
            ],
        },
    ], [statusFilter]);

    return (
        <TableLayout
            title={{ ar: "مقالات المدونة", en: "Blog Posts" }}
            columns={columns}
            items={blogs}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, blogs)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/blog/create")}
            addButtonLabel={{ ar: "إضافة مقال", en: "Add Blog Post" }}
            page={page} perPage={perPage} order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <BlogPostsTableRow
                    {...params} key={params.item.id} item={params.item}
                    perms={perms} onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default BlogPostsTable;
